import { $config } from '@client/core/atoms/config.js';
import {
  AdPlacement,
  Brand,
  debugLog,
  DeviceType,
  UNLEASH_FEATURE_NAME,
  type PlacementId
} from '@schibsted-nmp/advertising-shared';

import { isFeatureEnabled } from '../atoms/unleashFeatures.js';
import { isFinnTravelResultPage } from './travel/isFinnTravelResultPage.js';

/**
 * The threshold is a number between 0.0 and 1.0 inclusive, which indicates at what percentage of the target's
 * visibility the observer's callback should be executed. A threshold of 0.0 means that the callback will run
 * as soon as even one pixel is visible, and a threshold of 1.0 means that the callback will run when the entire target is visible.
 *
 * The rootMargin is a string which specifies a set of offsets to add to the root's bounding box when calculating intersections,
 * effectively shrinking or growing the area used for intersections. The syntax is similar to that of the CSS margin property,
 * and accepts values in pixels (px) or percentages (%). The values can be positive or negative, and they can be different for each side.
 * The order of the values is top, right, bottom, left.
 *
 * All placement will have different optimal settings.
 * Sometimes it might even differ per brand
 *
 * @param placementId
 */

const scrollMargins = {
  XXL: 800,
  XL: 600,
  LONG: 400,
  MEDIUM: 200,
  SHORT: 100,
  NONE: 0,
  DELAYED_SHORT: -100
};

export const noThreshold = 0.0;
export const SCROLL_MARGIN_TAKEOVER = '100px';

function getScrollMargin(length: number) {
  return `${length}px 0px ${length}px 0px`;
}

export const forceBatchRequestPlacements = new Array<PlacementId>(
  AdPlacement.AdvtTop1.id,
  AdPlacement.AdvtLeft1.id,
  AdPlacement.AdvtRight1.id,
  AdPlacement.AdvtWallpaper.id,
  AdPlacement.AdvtAudience.id
);

export type InViewSettings = {
  threshold: number;
  rootMargin: string;
} | null;

export function getInViewSettingsByBrand(
  placementId: PlacementId
): InViewSettings {
  const adConfig = $config.get();
  if (!adConfig) return null;

  if (
    isFeatureEnabled(UNLEASH_FEATURE_NAME.enableGamLazyLoading) &&
    !isFinnTravelResultPage()
  ) {
    return {
      threshold: noThreshold,
      rootMargin: getScrollMargin(scrollMargins.NONE)
    };
  }

  debugLog('Enabling in house lazy loading with in view settings');

  const { brand, deviceType } = adConfig;

  switch (brand) {
    case Brand.Finn:
      return getInViewSettingsForFinnByPlacementId(placementId, deviceType);

    case Brand.Tori:
      return getInViewSettingsForToriByPlacementId(placementId);

    case Brand.Dba:
      return getInViewSettingsForDbaByPlacementId(placementId, deviceType);

    default:
      return null;
  }
}

export function getInViewSettingsForDbaByPlacementId(
  placementId: PlacementId,
  deviceType: DeviceType
) {
  // search result placements
  if (placementId.includes('middle_')) {
    return {
      threshold: noThreshold,
      rootMargin:
        deviceType === DeviceType.Desktop
          ? getScrollMargin(scrollMargins.SHORT)
          : getScrollMargin(scrollMargins.MEDIUM)
    };
  }

  // recommendation placements
  if (placementId.includes('recirc')) {
    return {
      threshold: noThreshold,
      rootMargin:
        deviceType === DeviceType.Desktop
          ? getScrollMargin(scrollMargins.SHORT)
          : getScrollMargin(scrollMargins.MEDIUM)
    };
  }

  if (placementId === AdPlacement.AdvtBottom1.id) {
    return {
      threshold: noThreshold,
      rootMargin: getScrollMargin(scrollMargins.XL)
    };
  }

  const isSidePlacement2 =
    placementId === AdPlacement.AdvtRight2.id ||
    placementId === AdPlacement.AdvtLeft2.id;

  if (isSidePlacement2) {
    return {
      threshold: noThreshold,
      rootMargin:
        deviceType === DeviceType.Desktop
          ? getScrollMargin(scrollMargins.DELAYED_SHORT)
          : getScrollMargin(scrollMargins.NONE)
    };
  }

  // topbanner, skyscrapers etc
  return {
    threshold: noThreshold,
    rootMargin: getScrollMargin(scrollMargins.NONE)
  };
}

export function getInViewSettingsForFinnByPlacementId(
  placementId: PlacementId,
  deviceType: DeviceType
) {
  // search result placements
  if (placementId.includes('middle_')) {
    return {
      threshold: noThreshold,
      rootMargin:
        deviceType === DeviceType.Desktop
          ? getScrollMargin(scrollMargins.SHORT)
          : getScrollMargin(scrollMargins.MEDIUM)
    };
  }

  // recommendation placements
  if (placementId.includes('recirc')) {
    return {
      threshold: noThreshold,
      rootMargin: getScrollMargin(scrollMargins.SHORT)
    };
  }

  // topbanner, skyscrapers etc
  return {
    threshold: noThreshold,
    rootMargin: getScrollMargin(scrollMargins.NONE)
  };
}
export function getInViewSettingsForToriByPlacementId(
  placementId: PlacementId
) {
  switch (placementId) {
    case AdPlacement.AdvtTop2.id: {
      return {
        threshold: noThreshold,
        rootMargin: getScrollMargin(scrollMargins.NONE)
      };
    }
    case AdPlacement.AdvtMiddle1.id: {
      return {
        threshold: noThreshold,
        rootMargin: getScrollMargin(scrollMargins.LONG)
      };
    }
    case AdPlacement.AdvtMiddle2.id: {
      return {
        threshold: noThreshold,
        rootMargin: getScrollMargin(scrollMargins.LONG)
      };
    }
    case AdPlacement.AdvtRecircMiddle1.id: {
      return {
        threshold: noThreshold,
        rootMargin: getScrollMargin(scrollMargins.MEDIUM)
      };
    }
    case AdPlacement.AdvtRecircMiddle2.id: {
      return {
        threshold: noThreshold,
        rootMargin: getScrollMargin(scrollMargins.MEDIUM)
      };
    }
    case AdPlacement.AdvtBottom1.id: {
      return {
        threshold: noThreshold,
        rootMargin: getScrollMargin(scrollMargins.XL)
      };
    }
    case AdPlacement.AdvtLeft1.id:
    case AdPlacement.AdvtRight1.id:
    case AdPlacement.AdvtWallpaper.id: {
      return null;
    }
    default: {
      return {
        threshold: noThreshold,
        rootMargin: getScrollMargin(scrollMargins.NONE)
      };
    }
  }
}
